import React from 'react';
import { makeStyles } from '@mui/styles';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Button
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import moment from 'moment';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  tableContainer: {
    marginBottom: theme.spacing(3),
  },
  totalRow: {
    backgroundColor: theme.palette.action.hover,
  },
  headerCell: {
    fontWeight: 'bold',
  },
  averageRow: {
    backgroundColor: theme.palette.grey[50],
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  categoryTotal: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontWeight: 'bold',
  },
  chartContainer: {
    width: '100%',
    height: 400,
    marginBottom: theme.spacing(4),
  },
  exportButton: {
    marginLeft: 'auto',
    display: 'flex',
    marginBottom: theme.spacing(2),
  },
  headerContainer: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  }
}));

const PaymentTypesByTable = ({ reportData, startDate, endDate, isLoading }) => {
  const classes = useStyles();

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (date) => {
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  const exportToCSV = () => {
    // Prepare CSV data
    const csvRows = [];
    
    // Add headers
    csvRows.push(['Table Number', 'Payment Type', 'Amount', 'GST Amount']);
    
    // Add data rows
    sortedItems.forEach(tableData => {
      const tableNumber = tableData.tableNumber || 'Unknown';
      
      // Flatten payment types
      tableData.paymentTypes.forEach(payment => {
        // Format amount to 2 decimal places without currency symbol
        const formattedAmount = (payment.amount || 0).toFixed(2);
        // Calculate GST (amount / 11 since GST is 10% of the base price)
        const gstAmount = (payment.amount / 11 || 0).toFixed(2);
        
        csvRows.push([
          tableNumber,
          payment.type || 'Unknown',
          formattedAmount,
          gstAmount,
        ]);
      });
    });
    
    // Convert to CSV string
    const csvContent = csvRows.map(row => row.join(',')).join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `table_transactions_${moment(startDate).format('YYYYMMDD')}_${moment(endDate).format('YYYYMMDD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className={classes.root}>
        <Typography variant="h6" className={classes.title}>
          Payment Types Report by Table ({formatDate(startDate)} - {formatDate(endDate)})
        </Typography>
        <div className={classes.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  // Handle the new data format
  const items = reportData?.items || [];
  const totalAmount = reportData?.total || 0;

  // Sort items by table number
  const sortedItems = [...items].sort((a, b) => {
    const tableA = a.tableNumber || 'Unknown';
    const tableB = b.tableNumber || 'Unknown';
    
    // If both are numbers, sort numerically
    if (!isNaN(tableA) && !isNaN(tableB)) {
      return Number(tableA) - Number(tableB);
    }
    
    // If one or both are strings, sort alphabetically
    return tableA.toString().localeCompare(tableB.toString());
  });

  // Prepare data for the chart - get top 10 by amount
  const chartData = sortedItems
    .map(item => ({
      table: item.tableNumber || 'Unknown',
      amount: parseFloat(item.totalAmount || 0)
    }))
    .sort((a, b) => b.amount - a.amount)  // Sort by amount in descending order
    .slice(0, 10);  // Take only top 10

  if (!items || items.length === 0) {
    return (
      <div className={classes.root}>
        <Typography variant="h6" className={classes.title}>
          Payment Types Report by Table ({formatDate(startDate)} - {formatDate(endDate)})
        </Typography>
        <Typography variant="body1">
          No data available for the selected period.
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.headerContainer}>
        <Typography variant="h6" className={classes.title}>
          Payment Types Report by Table ({formatDate(startDate)} - {formatDate(endDate)})
        </Typography>
        <Button
          variant="contained"
          color="primary"
          className={classes.exportButton}
          startIcon={<GetAppIcon />}
          onClick={exportToCSV}
          disabled={isLoading || !items || items.length === 0}
        >
          Export to CSV
        </Button>
      </div>

      <Card className={classes.tableContainer}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Table Sales Overview
          </Typography>
          <div className={classes.chartContainer}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={chartData}
                margin={{
                  top: 20,
                  right: 30,
                  left: 60,
                  bottom: 60
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="table" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  interval={0}
                  tick={{
                    fontSize: 12,
                    dx: -8,
                    dy: 8
                  }}
                  label={{
                    value: "Table Number",
                    position: "bottom",
                    offset: 50
                  }}
                />
                <YAxis 
                  tickFormatter={(value) => formatCurrency(value)}
                  label={{
                    value: "Total Sales",
                    angle: -90,
                    position: "insideLeft",
                    offset: -40
                  }}
                />
                <Tooltip 
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => `Table ${label}`}
                  labelStyle={{ color: 'black' }}
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    padding: '10px'
                  }}
                />
                <Bar 
                  dataKey="amount" 
                  fill="#8884d8"
                  name="Total Sales"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
            <Typography 
              variant="caption" 
              align="center" 
              style={{ 
                display: 'block', 
                marginTop: 8,
                color: 'rgba(0, 0, 0, 0.6)'
              }}
            >
              Showing top 10 tables by sales amount out of {items.length} total tables
            </Typography>
          </div>
        </CardContent>
      </Card>

      {sortedItems.map((tableData) => {
        const tableNumber = tableData?.tableNumber || 'Unknown';
        const paymentTypes = tableData?.paymentTypes || [];
        const tableTotalAmount = tableData?.totalAmount || 0;
        const tableTotalTaxAmount = tableData?.taxAmount || 0;
        const transactionCount = tableData?.transactionCount || 0;
        const averageAmount = tableData?.averageAmount || '0.00';

        // Group payments by type and sum amounts
        const paymentsByType = paymentTypes.reduce((acc, payment) => {
          const type = payment.type || 'Unknown';
          if (!acc[type]) {
            acc[type] = {
              amount: 0,
              count: 0
            };
          }
          acc[type].amount += payment.amount;
          acc[type].count += 1;
          return acc;
        }, {});

        return (
          <Card key={tableNumber} className={classes.tableContainer}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Table {tableNumber}
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.headerCell}>Payment Type</TableCell>
                      <TableCell className={classes.headerCell} align="right">Count</TableCell>
                      <TableCell className={classes.headerCell} align="right">Total Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(paymentsByType).map(([type, data]) => (
                      <TableRow key={type}>
                        <TableCell>{type}</TableCell>
                        <TableCell align="right">{data.count}</TableCell>
                        <TableCell align="right">{formatCurrency(data.amount)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Total</strong></TableCell>
                      <TableCell align="right"><strong>{transactionCount}</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(tableTotalAmount)}</strong></TableCell>
                    </TableRow>
                    <TableRow className={classes.totalRow}>
                      <TableCell><strong>Total Tax Amount</strong></TableCell>
                      <TableCell align="right"><strong>{transactionCount}</strong></TableCell>
                      <TableCell align="right"><strong>{formatCurrency(tableTotalTaxAmount)}</strong></TableCell>
                    </TableRow>
                    <TableRow className={classes.averageRow}>
                      <TableCell><em>Average per Transaction</em></TableCell>
                      <TableCell align="right">-</TableCell>
                      <TableCell align="right"><em>{formatCurrency(parseFloat(averageAmount))}</em></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        );
      })}

      <Typography variant="h6" className={classes.categoryTotal}>
        Total for All Tables: {formatCurrency(totalAmount)}
      </Typography>
    </div>
  );
};

export default PaymentTypesByTable; 